import RouteMap from './workout/RouteMap';
import {
  formatDistance,
  formatFriendlyDuration,
  formatWorkoutPace,
  formatStartDateTime,
} from '../lib/utils';
import { Activity } from 'lucide-react';

interface WorkoutCardProps {
  workout: any;
  onClick: () => void;
}

const WorkoutCard = ({ workout, onClick }: WorkoutCardProps) => {
  const type = workout.type || 'workout';
  const workoutTime = workout.elapsedTime || workout.movingTime || 0;
  const hasMap =
    workout.polylineEncoded ||
    (workout.startLatitude && workout.startLongitude);

  return (
    <div
      className="surface-card group relative z-0 cursor-pointer p-5 transition-all hover:-translate-y-0.5 hover:shadow-xl"
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
    >
      {/* Top section: map on left, title+date on right, type badge top right */}
      <div className="flex gap-4">
        {/* Map on left */}
        <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-xl sm:h-16 sm:w-16 md:h-20 md:w-20">
          {hasMap ? (
            <RouteMap
              polylineEncoded={workout.polylineEncoded}
              startLatitude={workout.startLatitude}
              startLongitude={workout.startLongitude}
              endLatitude={workout.endLatitude}
              endLongitude={workout.endLongitude}
              className="w-full h-full"
              showMarkers={false}
              showAttribution={false}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-brand-blueSoft text-xs text-brand-cobalt">
              No route
            </div>
          )}
        </div>

        {/* Title + date on right */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="truncate font-bold text-brand-midnight transition-colors group-hover:text-brand-cobalt md:text-lg">
              {workout.title || 'Untitled Workout'}
            </h3>
            <div className="flex items-center gap-1 flex-shrink-0">
              <span className="inline-flex items-center gap-1 rounded-full bg-brand-midnight px-2.5 py-1 text-xs font-bold capitalize text-white">
                <Activity className="h-3 w-3 text-brand-volt" />
                {type}
              </span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1 md:text-sm">
            {formatStartDateTime(workout.completedAt)}
          </p>
        </div>
      </div>

      {/* Bottom section: distance, time, average pace */}
      <div className="mt-4 grid grid-cols-3 divide-x divide-gray-200 border-t border-gray-100 pt-4">
        <div className="text-center first:pl-0">
          <div className="text-xs text-gray-500 md:text-sm">Distance</div>
          <div className="text-sm font-semibold text-gray-900 md:text-base">
            {formatDistance(workout.distance)}
          </div>
        </div>
        <div className="text-center">
          <div className="text-xs text-gray-500 md:text-sm">Time</div>
          <div className="text-sm font-semibold text-gray-900 md:text-base">
            {formatFriendlyDuration(workoutTime)}
          </div>
        </div>
        <div className="text-center last:pr-0">
          <div className="text-xs text-gray-500 md:text-sm">Avg Pace</div>
          <div className="text-sm font-semibold text-gray-900 md:text-base">
            {formatWorkoutPace(workout.type, workout.distance, workoutTime)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkoutCard;
